<template>
  <form class="ak-form" novalidate @submit.prevent="handleSubmission($event)">
    <slot :is-invalid="isInvalid" />
  </form>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-form :submit="submitForm">
   *  <template slot="fields">
   *   <ak-field type="info" label="Name">
   *     <ak-input v-model="name" />
   *   </ak-field>
   *   <ak-field type="info" label="Email">
   *     <ak-input v-model="email" />
   *   </ak-field>
   *   <ak-field>
   *     <ak-button type="submit">
   *       Submit
   *     </ak-button>
   *   </ak-field>
   *  </template>
   * </ak-form>
   */
  name: 'AkForm',

  /**
   * @return Object
   * @description Provides the returned object to the components decendants.
   */
  provide() {
    return {
      akForm: this,
    }
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Function}
     * @description The submit method to use when the form is submitted by a
     * user, this will be executed by the "handleSubmission" method.
     */
    submit: {
      type: Function,
      default: /* istanbul ignore next */ () => {},
    },

    /**
     * @type {Boolean}
     * @description This will hold the valid state of the form that will be
     * bound to the vue model
     */
    value: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['submit'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes for the component.
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const isInvalid = ref(props.value)
    const isForm = ref(true)
    const hasParts = ref(false)
    const activePartNumber = ref(0)
    const activePart = ref(false)
    const parts = ref([])
    const inputs = ref([])
    const watchers = ref([])
    const errors = ref({})
    const submitHooks = []
    const validationErrorHooks = []
    const validHooks = []

    /**
     * @returns {String}
     * @description Returns the part count text.
     */
    const currentPage = computed(() => {
      if (!hasParts.value) return ''
      const index = parts.value.findIndex((i) => i.uid === activePart.value.uid)
      if (index >= 0) return `${index + 1}/${parts.value.length}`
      return ''
    })

    /**
     * @param {Event} event
     * @description Handles the submission of the form, this will try to run
     * the validation on all of the fields before submitting anything.
     */
    const handleSubmission = async (event) => {
      if (event && event instanceof Event) {
        event.preventDefault()
        event.stopPropagation()
      }

      const res = await validate(true)
      const invalid = res.some((i) => i === false)
      isInvalid.value = invalid
      if (invalid) {
        await onValidationErrorHook()
        return
      } else {
        await onValidHook()
      }

      if (hasParts.value) {
        const index = parts.value.findIndex((i) => i === activePart.value)
        if (index >= 0) {
          const newIndex = index + 1
          const nextPart = parts.value[newIndex]
          activePartNumber.value = newIndex
          if (nextPart) {
            activePart.value = nextPart
            return
          }
        }
      }

      // beforeSubmit handlers that return false can prevent the form from
      // being submit
      const preventSubmit = await onBeforeSubmitHook()
      if (!preventSubmit) {
        emit('submit', event)
      }
    }

    /**
     * @returns {Boolean}
     * @description Tries to validate all of the fields in the form
     * component
     */
    const validate = async (forceDisplay = false) => {
      return Promise.all(
        inputs.value.map(async (i) => await validateInput(i, forceDisplay))
      )
    }

    /**
     * @returns {Boolean}
     * @description Tries to run the validation on an input in the form
     * component.
     */
    const validateInput = async (input, forceDisplay) => {
      /* istanbul ignore else */
      if (typeof input.validate === 'function') {
        return await input.validate(forceDisplay)
      } else {
        return true
      }
    }

    /**
     * @param {Object} part
     * @description Registers a form part component
     */
    const registerPart = (part) => {
      parts.value.push(part)
      hasParts.value = true
      if (!activePart.value) {
        activePart.value = part
      }
    }

    /**
     * @param {Object} input
     * @description Registers a new input into the form component
     */
    const register = (input) => {
      inputs.value.push(input)
      errors.value[input.uuid] = !input.isValid
    }

    /**
     * @param {Object} input
     * @description Un-Registers an existing input component
     */
    const unregister = (uuid) => {
      const found = inputs.value.find((i) => i.uuid === uuid)
      /* istanbul ignore if */
      if (!found) return
      inputs.value = inputs.value.filter((i) => i.uuid !== found.uuid)
      delete errors.value[found.uuid]
    }

    /**
     * @description Calls all of the beforeSubmit handlers these should be
     * async methods that return true or false, a return value of false in
     * any of the submitHooks will prevent the form submission.
     */
    const onBeforeSubmitHook = async () => {
      try {
        const res = await Promise.all(submitHooks.map((i) => i()))
        return res.some((i) => i === false)
      } catch (ex) {
        console.error(ex)
        return false
      }
    }

    /**
     * @param {Function} handler - Async function to call
     * @description Adds a new beforeSubmit handler to the form, these are
     * called after validation, but before submit.
     */
    const onBeforeSubmit = (handler) => {
      if (typeof handler === 'function') {
        submitHooks.push(handler)
      }
    }

    /**
     * @description Calls all of the onValidationError handlers these should be
     * async methods that return true or false, a return value of false in
     * any of the submitHooks will prevent the form submission.
     */
    const onValidationErrorHook = async () => {
      try {
        const res = await Promise.all(validationErrorHooks.map((i) => i()))
        return res.some((i) => i === false)
      } catch (ex) {
        console.error(ex)
        return false
      }
    }

    /**
     * @param {Function} handler - Async function to call
     * @description Adds a new validationError handler to the form, these are
     * called on failed validation of the form
     */
    const onValidationError = (handler) => {
      if (typeof handler === 'function') {
        validationErrorHooks.push(handler)
      }
    }

    const onValidHook = async () => {
      try {
        const res = await Promise.all(validHooks.map((i) => i()))
        return res.some((i) => i === false)
      } catch (ex) {
        console.error(ex)
        return false
      }
    }

    /**
     * @param {Function} handler  - Async function to call
     * @description Adds a new form valid hook, these are called when the form
     * fields have been validated correctly.
     */
    const onValid = (handler) => {
      if (typeof handler === 'function') {
        validHooks.push(handler)
      }
    }

    return {
      isInvalid,
      isForm,
      hasParts,
      activePartNumber,
      activePart,
      parts,
      inputs,
      watchers,
      errors,
      currentPage,
      handleSubmission,
      validate,
      validateInput,
      registerPart,
      register,
      unregister,
      onBeforeSubmit,
      onValidationError,
      onValid,
    }
  },
})
</script>
